import {Component, forwardRef, inject, Input, OnInit} from '@angular/core';
import { NG_VALUE_ACCESSOR, Validators, FormControl, FormGroup, FormBuilder, FormsModule, ReactiveFormsModule } from '@angular/forms';
import {UntilDestroy} from '@ngneat/until-destroy';
import {Subscription} from 'rxjs';
import {
  createNoDecimalInputMask,
  createTwoDecimalInputMask,
  formControlErrorKeys,
  formControlErrorMessage,
  fromRateCardCell,
  rateOrRelativeRateValidator,
  toRateCardCell,
  Mark
} from '@portal-workspace/grow-ui-library';
import {setupUntilDestroy} from '@portal-workspace/grow-ui-library';
import {AbstractControlValueAccessor} from '../abstract-control-value-accessor';
import {delay, distinctUntilChanged, tap} from 'rxjs/operators';
import numeral from 'numeral';
import {AssetTier3Value, compareMatch} from '@portal-workspace/grow-shared-library';
import {MARK} from '@portal-workspace/grow-ui-library/mark';
import { MatDividerModule } from '@angular/material/divider';
import { FlexModule } from '@angular/flex-layout/flex';
import { InputMaskModule } from '@ngneat/input-mask';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';




// 5.7.0: 4-bucket AF Tier 3 rate card mirroring Tier 1/2 — a single `New` rate
// plus three age-based `Used` buckets bounded by the editable Year1/Year2
// thresholds. Solar is no longer priced separately.
@UntilDestroy({arrayName: 'subscriptions'})
@Component({
  selector: 'asset-tier3',
  templateUrl: './asset-tier3.component.html',
  styleUrls: ['./asset-tier3.component.scss'],
  providers: [
    { provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => AssetTier3Component), multi: true },
    { provide: MARK, useExisting: forwardRef(() => AssetTier3Component), multi: false },
  ],
  standalone: true,
  imports: [MatFormFieldModule, MatInputModule, FormsModule, InputMaskModule, ReactiveFormsModule, FlexModule, MatDividerModule]
})
export class AssetTier3Component extends AbstractControlValueAccessor<AssetTier3Value> implements OnInit, Mark {

  errorKeys = formControlErrorKeys;
  errorMessage = formControlErrorMessage;

  @Input({required: false}) title: string = '';
  @Input({required: false}) maxYear: number = 20;

  subscriptions: Subscription[] = [];

  private formBuilder: FormBuilder;

  createNoDecimalInputMask = createNoDecimalInputMask();
  createTwoDecimalInputMask = createTwoDecimalInputMask();

  formGroup: FormGroup<{
    newRate: FormControl<number | null>,
    year1: FormControl<number | null>,
    year2: FormControl<number | null>,
    assetBackedLowYear: FormControl<string | null>,
    assetBackedMediumYear: FormControl<string | null>,
    assetBackedHighYear: FormControl<string | null>,
  }>;
  formControlNewRate: FormControl<number | null>;
  formControlYear1: FormControl<number | null>;
  formControlYear2: FormControl<number | null>;
  formControlAssetBackedLowYear: FormControl<string | null>;
  formControlAssetBackedMediumYear: FormControl<string | null>;
  formControlAssetBackedHighYear: FormControl<string | null>;

  constructor() {
    super();
    this.formBuilder = inject(FormBuilder);
    this.formControlNewRate = this.formBuilder.control(0, [Validators.required]);
    this.formControlYear1 = this.formBuilder.control(4, [Validators.required]);
    this.formControlYear2 = this.formBuilder.control(9, [Validators.required]);
    this.formControlAssetBackedLowYear = this.formBuilder.control<string | null>('0', [Validators.required, rateOrRelativeRateValidator]);
    this.formControlAssetBackedMediumYear = this.formBuilder.control<string | null>('0', [Validators.required, rateOrRelativeRateValidator]);
    this.formControlAssetBackedHighYear = this.formBuilder.control<string | null>('0', [Validators.required, rateOrRelativeRateValidator]);
    this.formGroup = this.formBuilder.group({
      newRate: this.formControlNewRate,
      year1: this.formControlYear1,
      year2: this.formControlYear2,
      assetBackedLowYear: this.formControlAssetBackedLowYear,
      assetBackedMediumYear: this.formControlAssetBackedMediumYear,
      assetBackedHighYear: this.formControlAssetBackedHighYear,
    });
  }

  ngOnInit(): void {
    setupUntilDestroy(this);
    // maxYear is an @Input, so this validator must be wired here (not in the
    // constructor) to pick up the value passed in by the parent template.
    this.formControlYear2.addValidators(Validators.max(this.maxYear));
    this.formControlYear2.updateValueAndValidity({emitEvent: false});
    const sub = this.formGroup.valueChanges.pipe(
      delay(0),
      distinctUntilChanged(compareMatch),
      tap(r  => {
        if (this.formGroup.valid) {
          // NOTE: when formGroup is valid, values should not be null due to validators configured
          const v: Exclude<AssetTier3Value, null> = {
            YearRangeThresholds: [String(r.year1), String(r.year2)],
            AssetBacked: {
              New: { Rate: (this.formControlNewRate.value ?? 0).toString() },
              YearRanges: [
                {
                  YearRange: `Used to ${r.year1} years`,
                  YearRangeType: 'Low',
                  Operator: '<=',
                  Threshold: `${r.year1}`,
                  Rate: toRateCardCell(r.assetBackedLowYear),
                },
                {
                  YearRange: `${this.parseToInt(r.year1) + 1} to ${r.year2} years`,
                  YearRangeType: 'Medium',
                  Operator: '-',
                  Threshold: `${this.parseToInt(r.year1) + 1}-${r.year2}`,
                  Rate: toRateCardCell(r.assetBackedMediumYear),
                },
                {
                  YearRange: `${this.parseToInt(r.year2) + 1} to ${this.maxYear} years`,
                  YearRangeType: 'High',
                  Operator: '>',
                  Threshold: `${r.year2}`,
                  Rate: toRateCardCell(r.assetBackedHighYear),
                },
              ]
            },
          };
          this.propagateChange(v);
        } else {
          this.propagateChange(null);
        }
      })
    ).subscribe();

  }

  doWriteValue(v: AssetTier3Value | null | undefined): void | AssetTier3Value {
    if (v) {
      const newRate = v.AssetBacked?.New?.Rate;
      if (newRate != null) {
        this.formControlNewRate.setValue(numeral(newRate).value());
      }
      if (v.YearRangeThresholds && v.YearRangeThresholds.length) {
        if (v.YearRangeThresholds.length >= 1 && numeral(v.YearRangeThresholds[0]).value()) {
          this.formControlYear1.setValue(numeral(v.YearRangeThresholds[0]).value());
        }
        if (v.YearRangeThresholds.length >= 2 && numeral(v.YearRangeThresholds[1]).value()) {
          this.formControlYear2.setValue(numeral(v.YearRangeThresholds[1]).value());
        }
      }
      if (v.AssetBacked) {
        const lowYear = (v.AssetBacked.YearRanges ?? []).find(opt => opt.YearRangeType === 'Low');
        const lowYearRate = lowYear ? fromRateCardCell(lowYear.Rate) : null;
        if (lowYearRate != null) {
          this.formControlAssetBackedLowYear.setValue(lowYearRate);
        }
        const mediumYear = (v.AssetBacked.YearRanges ?? []).find(opt => opt.YearRangeType === 'Medium');
        const mediumYearRate = mediumYear ? fromRateCardCell(mediumYear.Rate) : null;
        if (mediumYearRate != null) {
          this.formControlAssetBackedMediumYear.setValue(mediumYearRate);
        }
        const highYear = (v.AssetBacked.YearRanges ?? []).find(opt => opt.YearRangeType === 'High');
        const highYearRate = highYear ? fromRateCardCell(highYear.Rate) : null;
        if (highYearRate != null) {
          this.formControlAssetBackedHighYear.setValue(highYearRate);
        }
      }
    }
    return undefined;
  }

  parseToInt(i: any): number {
    return parseInt(i, 10);
  }

  mark() {
    this.formGroup.markAllAsTouched();
  }
}
